<script>
import { getContext, setContext } from "svelte";
import { MENU_PANEL_KEY } from "./menuContext.js";

/**
 * @type {{
 *   label: string,
 *   icon?: string,
 *   disabled?: boolean,
 *   disabledReason?: string,
 *   children: import('svelte').Snippet,
 * }}
 */
let {
    label,
    icon = "",
    disabled = false,
    disabledReason = "",
    children,
} = $props();

const parentPanel = getContext(MENU_PANEL_KEY);
const panelState = $state({ openChildId: /** @type {string | null} */ (null) });
setContext(MENU_PANEL_KEY, {
    get menuId() {
        return parentPanel?.menuId;
    },
    get openChildId() {
        return panelState.openChildId;
    },
    openChild(childId) {
        panelState.openChildId = childId;
    },
    closeChild(childId) {
        if (panelState.openChildId === childId) {
            panelState.openChildId = null;
        }
    },
});

const myId = $props.id();
/** @type {HTMLDivElement | null} */
let panelEl = null;
let openLeft = $state(false);
let openTimer;
let closeTimer;
let isOpen = $derived(parentPanel.openChildId === myId);

function clearTimers() {
    clearTimeout(openTimer);
    clearTimeout(closeTimer);
}

function openNow() {
    if (disabled) {
        return;
    }
    clearTimers();
    parentPanel.openChild(myId);
}

function closeNow() {
    clearTimers();
    if (parentPanel.openChildId === myId) {
        parentPanel.closeChild(myId);
    }
    panelState.openChildId = null;
}

function onEnter() {
    if (disabled) {
        return;
    }
    clearTimers();
    openTimer = setTimeout(openNow, 200);
}

function onLeave() {
    clearTimers();
    closeTimer = setTimeout(closeNow, 200);
}

function onTriggerClick(event) {
    event.preventDefault();
    event.stopPropagation();
    if (disabled) {
        return;
    }
    if (isOpen) {
        closeNow();
        return;
    }
    openNow();
}

/** @type {import('svelte/attachments').Attachment<HTMLAnchorElement>} */
function attachTrigger(node) {
    function onOpen() {
        openNow();
    }

    function onClose() {
        closeNow();
    }

    node.addEventListener("cc-menu-open", onOpen);
    node.addEventListener("cc-menu-close", onClose);
    return () => {
        node.removeEventListener("cc-menu-open", onOpen);
        node.removeEventListener("cc-menu-close", onClose);
    };
}

/** @type {import('svelte/attachments').Attachment<HTMLDivElement>} */
function attachPanel(node) {
    panelEl = node;
    return () => {
        panelEl = null;
    };
}

$effect(() => {
    if (!isOpen) {
        if (panelState.openChildId !== null) {
            panelState.openChildId = null;
        }
        if (openLeft) {
            openLeft = false;
        }
        return;
    }

    requestAnimationFrame(() => {
        if (!panelEl) {
            return;
        }
        const rect = panelEl.getBoundingClientRect();
        const shouldOpenLeft = rect.right > window.innerWidth - 8;
        if (openLeft !== shouldOpenLeft) {
            openLeft = shouldOpenLeft;
        }
    });
});
</script>

<div
    class={["dropdown-submenu", isOpen && "show", openLeft && "drop-start"]}
    data-submenu="true"
    role="none"
    onmouseenter={onEnter}
    onmouseleave={onLeave}>
    <a
        {@attach attachTrigger}
        class="dropdown-item cc-submenu-trigger"
        href="#!/"
        role="menuitem"
        tabindex="-1"
        data-submenu-trigger
        aria-haspopup="true"
        aria-expanded={isOpen}
        aria-disabled={disabled}
        title={disabled && disabledReason ? disabledReason : undefined}
        onclick={onTriggerClick}>
        <span class="cc-submenu-icon" aria-hidden="true">
            {#if icon}
                <i class="bi {icon}"></i>
            {/if}
        </span>
        <span class="cc-submenu-label">{label}</span>
        <i class="bi bi-chevron-right cc-submenu-chevron" aria-hidden="true"></i>
    </a>
    <div
        {@attach attachPanel}
        class={["dropdown-menu", isOpen && "show"]}
        role="menu"
        data-submenu="true">
        {@render children()}
    </div>
</div>

<style>
.dropdown-submenu {
    position: relative;
}

.dropdown-submenu > .dropdown-menu {
    position: absolute;
    top: 0;
    left: 100%;
    margin-top: -0.125rem;
    margin-left: 0;
    z-index: 1001;
    overflow: visible;
}

.dropdown-submenu.drop-start > .dropdown-menu {
    left: auto;
    right: 100%;
}

a.cc-submenu-trigger {
    display: flex;
    align-items: center;
    gap: 0.5rem;
}

.cc-submenu-trigger[aria-disabled="true"] {
    opacity: 0.5;
    cursor: not-allowed;
}

.cc-submenu-icon {
    width: 1.1em;
    flex-shrink: 0;
    text-align: center;
}

.cc-submenu-label {
    flex: 1;
    min-width: 0;
}

.cc-submenu-chevron {
    font-size: 0.7em;
    opacity: 0.7;
    margin-left: 0.75rem;
}
</style>
